// CR036: Scan Settings Panel for Flyer Diagnostics v2

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Settings, Zap, Target, Shield, Save, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import type { PrimaryOcrEngine, CandidateScoringConfig } from '@/lib/flyer-pipeline-v2/types';
import { CandidateSignalBuilder } from './CandidateSignalBuilder';

// Available AI models for page scanning
export const AI_SCAN_MODELS = [
  { value: 'google/gemini-3-flash-preview', label: 'Gemini 3 Flash', description: 'Fast, balanced quality (default)' },
  { value: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash', description: 'Good multimodal, lower cost' },
  { value: 'google/gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite', description: 'Fastest, cheapest option' },
  { value: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro', description: 'Highest quality, slower' },
  { value: 'google/gemini-3-pro-preview', label: 'Gemini 3 Pro', description: 'Next-gen, best reasoning' },
  { value: 'openai/gpt-5-mini', label: 'GPT-5 Mini', description: 'OpenAI balanced option' },
  { value: 'openai/gpt-5', label: 'GPT-5', description: 'OpenAI premium quality' },
] as const;

export type AiScanModel = typeof AI_SCAN_MODELS[number]['value'];

export interface ScanSettingsV2 {
  primaryOcrEngine: PrimaryOcrEngine;
  aiPageScanEnabled: boolean;
  aiModel: AiScanModel;
  maxAiCalls: number;
  candidateScoringConfig: CandidateScoringConfig;
}

interface ScanSettingsPanelV2Props {
  settings: ScanSettingsV2;
  onChange: (settings: ScanSettingsV2) => void;
  disabled?: boolean;
}

const PRIMARY_OCR_ENGINES: { value: PrimaryOcrEngine; label: string; description: string }[] = [
  { value: 'tesseract', label: 'Tesseract', description: 'Open-source OCR engine (runs in browser)' },
  { value: 'none', label: 'None', description: 'Skip primary OCR (use for testing)' },
];

export function ScanSettingsPanelV2({ settings, onChange, disabled }: ScanSettingsPanelV2Props) {
  const [expanded, setExpanded] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  const updateSetting = <K extends keyof ScanSettingsV2>(key: K, value: ScanSettingsV2[K]) => {
    onChange({ ...settings, [key]: value });
  };

  const updateCandidateConfig = (config: CandidateScoringConfig) => {
    onChange({ ...settings, candidateScoringConfig: config });
  };

  // Handle AI model change with auto-save to app_config
  const handleModelChange = async (model: AiScanModel) => {
    updateSetting('aiModel', model);
    
    try {
      // Save to app_config for persistence
      const { error } = await supabase
        .from('app_config')
        .upsert({
          key: 'flyer_v2_ai_model',
          value_json: { model },
          updated_at: new Date().toISOString(),
        }, { onConflict: 'key' });
      
      if (error) throw error;
      
      toast({
        title: 'AI Model Updated',
        description: `Now using ${AI_SCAN_MODELS.find(m => m.value === model)?.label || model}`,
      });
    } catch (err) {
      console.error('Failed to save AI model preference:', err);
      toast({
        title: 'Save failed',
        description: 'Model changed locally but failed to persist',
        variant: 'destructive',
      });
    }
  };

  const handleSaveConfig = async () => {
    setIsSaving(true);
    try {
      // First, set all existing configs to inactive
      await supabase
        .from('flyer_candidate_scoring_config')
        .update({ is_active: false })
        .eq('is_active', true);

      // Create new version with incremented version number
      const newVersion = `v${Date.now()}`;
      
      const { error } = await supabase
        .from('flyer_candidate_scoring_config')
        .insert([{
          version: newVersion,
          enabled: settings.candidateScoringConfig.enabled,
          threshold: settings.candidateScoringConfig.threshold,
          min_primary_ocr_score: settings.candidateScoringConfig.min_primary_ocr_score,
          signals: JSON.parse(JSON.stringify(settings.candidateScoringConfig.signals)),
          is_active: true,
        }]);

      if (error) throw error;

      toast({
        title: 'Configuration saved',
        description: `Candidate scoring config ${newVersion} is now active. It will be used in the next scan.`,
      });
    } catch (err) {
      console.error('Failed to save config:', err);
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="bg-muted/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Settings className="h-4 w-4" />
          Scan Settings (v2)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Primary OCR Engine */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1">
              <Zap className="h-3 w-3" />
              Primary OCR Engine
            </Label>
            <Select
              value={settings.primaryOcrEngine}
              onValueChange={(v) => updateSetting('primaryOcrEngine', v as PrimaryOcrEngine)}
              disabled={disabled}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIMARY_OCR_ENGINES.map((engine) => (
                  <SelectItem key={engine.value} value={engine.value}>
                    <div className="flex flex-col">
                      <span>{engine.label}</span>
                      <span className="text-xs text-muted-foreground">{engine.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1">
              <Target className="h-3 w-3" />
              Max AI Calls
            </Label>
            <Input
              type="number"
              min={1}
              max={100}
              value={settings.maxAiCalls}
              onChange={(e) => updateSetting('maxAiCalls', parseInt(e.target.value) || 50)}
              disabled={disabled}
              className="h-8 text-xs"
            />
          </div>
        </div>

        {/* AI Page Scan Toggle */}
        <div className="flex items-center justify-between py-2 border-t">
          <div>
            <Label className="text-xs">AI Page Scan</Label>
            <p className="text-xs text-muted-foreground">
              Use AI Vision to analyze candidate pages
            </p>
          </div>
          <Switch
            checked={settings.aiPageScanEnabled}
            onCheckedChange={(v) => updateSetting('aiPageScanEnabled', v)}
            disabled={disabled}
          />
        </div>

        {/* AI Model Selector - only show when AI is enabled */}
        {settings.aiPageScanEnabled && (
          <div className="space-y-2 py-2 border-t">
            <Label className="text-xs flex items-center gap-1">
              <Zap className="h-3 w-3" />
              AI Model
            </Label>
            <Select
              value={settings.aiModel}
              onValueChange={(v) => handleModelChange(v as AiScanModel)}
              disabled={disabled}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AI_SCAN_MODELS.map((model) => (
                  <SelectItem key={model.value} value={model.value}>
                    <div className="flex flex-col">
                      <span>{model.label}</span>
                      <span className="text-xs text-muted-foreground">{model.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Model selection auto-saves. Cheaper models may miss some deals.
            </p>
          </div>
        )}

        {/* Candidate Detection Accordion */}
        <Accordion type="multiple" value={expanded} onValueChange={setExpanded} className="border-t pt-2">
          <AccordionItem value="summary" className="border-b-0">
            <AccordionTrigger className="py-2 text-xs hover:no-underline">
              <div className="flex items-center gap-2">
                <Shield className="h-3 w-3" />
                Candidate Detection Summary
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Candidate Scoring Enabled</Label>
                  <Switch
                    checked={settings.candidateScoringConfig.enabled}
                    onCheckedChange={(v) =>
                      updateCandidateConfig({ ...settings.candidateScoringConfig, enabled: v })
                    }
                    disabled={disabled}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Threshold</Label>
                    <Input
                      type="number"
                      min={0}
                      step={0.5}
                      value={settings.candidateScoringConfig.threshold}
                      onChange={(e) =>
                        updateCandidateConfig({
                          ...settings.candidateScoringConfig,
                          threshold: parseFloat(e.target.value) || 7,
                        })
                      }
                      disabled={disabled}
                      className="h-7 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Min OCR Score</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={settings.candidateScoringConfig.min_primary_ocr_score}
                      onChange={(e) =>
                        updateCandidateConfig({
                          ...settings.candidateScoringConfig,
                          min_primary_ocr_score: parseFloat(e.target.value) || 25,
                        })
                      }
                      disabled={disabled}
                      className="h-7 text-xs"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      updateCandidateConfig({
                        ...settings.candidateScoringConfig,
                        threshold: 7,
                        min_primary_ocr_score: 25,
                      });
                    }}
                    disabled={disabled}
                  >
                    Reset to Defaults
                  </Button>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="signals" className="border-b-0">
            <AccordionTrigger className="py-2 text-xs hover:no-underline">
              <div className="flex items-center gap-2">
                Signals ({settings.candidateScoringConfig.signals.filter((s) => s.enabled).length} active)
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <CandidateSignalBuilder
                signals={settings.candidateScoringConfig.signals}
                onChange={(signals) =>
                  updateCandidateConfig({ ...settings.candidateScoringConfig, signals })
                }
                disabled={disabled}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Save Configuration Button */}
        <div className="border-t pt-3">
          <Button
            onClick={handleSaveConfig}
            disabled={disabled || isSaving}
            className="w-full"
            size="sm"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Configuration
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-2">
            Save to persist changes. The next scan will use the saved configuration.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
